import { Button, Flex, Text, Card, Separator } from "@radix-ui/themes";
import { Pencil2Icon } from "@radix-ui/react-icons";
import {
  CreateExploreView,
  ExploreCustomField,
  ExploreView,
} from "@/v2/entities/explore/Explore.model";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/dataTabs/tabs";
import {
  ExploreFilterMenu,
  ExploreFiltersValue,
} from "../explore-filter-menu/ExploreFilterMenu";
import { InlineTextField } from "@/components/shared/inputs/inilie-text-field/InlineTextField";
import { TabItem } from "@/v2/components/tabs/Tabs";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { ExploreDisplayOptions } from "./explore-display-options/ExploreDisplayOptions";
import { ViewFormData, ViewFormErrors } from "./useExploreViewForm";
import { useMemo } from "react";
import { DisplayOptions } from "./explore-display-options/ExploreDisplayOptions.model";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { ExploreColumnType } from "@/v2/entities/explore/columns/ExploreColum.model";
import {
  defaultConversationsColumns,
  defaultTicketsColumns,
} from "./explore-display-options/ExploreDisplayOptions.consts";

interface ExploreNewViewFormProps {
  mode: "create" | "update";
  formData: ViewFormData;
  displayOptions: DisplayOptions;
  filters: ExploreFiltersValue;
  filter: Filter | undefined;
  errors: ViewFormErrors;
  isLoading: boolean;
  customFields: ExploreCustomField[];
  onSubmit: () => void;
  onCancel: () => void;
  onFieldChange: <K extends keyof ViewFormData>(
    field: K,
    value: ViewFormData[K]
  ) => void;
  onDisplayOptionsChange: (newOptions: DisplayOptions) => void;
  onFiltersChange: (newFilters: ExploreFiltersValue) => void;
}

export default function ExploreNewViewForm(props: ExploreNewViewFormProps) {
  const {
    mode,
    formData,
    errors,
    isLoading,
    filters,
    displayOptions,
    filter,
    customFields,
    onFiltersChange,
    onDisplayOptionsChange,
    onFieldChange,
    onSubmit,
    onCancel,
  } = props;

  const sourceTabs: TabItem[] = [
    {
      value: "tickets",
      label: "Tickets",
      icon: <Ticket03Icon fill="blue" />,
    },
    {
      value: "conversations",
      label: "Conversations",
      icon: <Comment01Icon fill="orange" />,
    },
  ];

  const selectedColumns = useMemo(() => {
    if (
      displayOptions &&
      displayOptions.columns &&
      displayOptions.columns.length > 0
    ) {
      return displayOptions.columns;
    }

    const dynamicColumns = customFields?.map(
      (customField) => customField.id as ExploreColumnType
    );
    return formData.source === "conversations"
      ? [...defaultConversationsColumns, ...(dynamicColumns ?? [])]
      : [...defaultTicketsColumns, ...(dynamicColumns ?? [])];
  }, [displayOptions.columns, formData.source, customFields]);

  const headerTitle = useMemo(
    () => (props.mode === "create" ? "New View" : "Edit View"),
    [props.mode]
  );

  return (
    <div className="w-full">
      <Card className="m-2">
        <Flex direction="column" gap="2">
          <Flex justify="between">
            <label>
              <Text as="div" size="2" mb="2" weight="bold">
                {headerTitle}
              </Text>
              <InlineTextField
                size={"2"}
                value={formData.title}
                onChange={(value) => onFieldChange("title", value)}
                placeholder="All tickets"
                icon={<Pencil2Icon width={16} height={16} className="ml-2" />}
              />
              {errors.title && (
                <Text size="1" color="red" mt="1">
                  {errors.title}
                </Text>
              )}
            </label>

            <Flex gap="2">
              <Button
                variant="soft"
                color="gray"
                onClick={onCancel}
                disabled={isLoading || props.isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={onSubmit}
                loading={isLoading || props.isLoading}
                highContrast
              >
                {props.mode === "create" ? "Create View" : "Save Changes"}
              </Button>
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" />

          <div className="flex justify-between">
            <div className="flex items-center gap-1">
              <Tabs
                value={formData.source}
                onValueChange={(value) =>
                  onFieldChange("source", value as "tickets" | "conversations")
                }
                defaultValue="tickets"
              >
                <TabsList size="md" defaultValue="tickets">
                  {sourceTabs.map((tab) => (
                    <TabsTrigger key={tab.value} value={tab.value}>
                      {tab.icon}
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <ExploreFilterMenu
                source={formData.source as "tickets" | "conversations"}
                value={filters}
                onChange={onFiltersChange}
                filter={filter}
                size="2"
                label="Add Filter"
                hideCount={false}
              />
              {errors.source && (
                <Text size="1" color="red" mt="1">
                  {errors.source}
                </Text>
              )}
            </div>
            <ExploreDisplayOptions
              value={displayOptions}
              onChange={onDisplayOptionsChange}
              source={formData.source}
              selectedColumns={selectedColumns}
            />
          </div>
        </Flex>
      </Card>
    </div>
  );
}
